import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { getViteApiUrl } from '../../config/env';

interface FileUploadProps {
  currentFile?: string; // URL del archivo actual (para preview de archivos existentes)
  onFileChange: (file: File | null) => void; // File real o null para eliminar
  accept?: string; // Tipos de archivo aceptados, ej: ".pdf,.jpg,.png" o "application/pdf,image/*"
  maxSizeMB?: number;
  label?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  currentFile,
  onFileChange,
  accept = ".pdf,.jpg,.jpeg,.png",
  maxSizeMB = 10,
  label,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const API_BASE_URL = getViteApiUrl();
  // Si currentFile es una URL relativa (empieza con /), construir la URL completa
  const previewUrl = currentFile?.startsWith('/') ? `${API_BASE_URL}${currentFile}` : currentFile;
  const [preview, setPreview] = useState<string | null>(previewUrl || null);
  const [fileName, setFileName] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Actualizar preview cuando currentFile cambia (para archivos existentes)
  useEffect(() => {
    if (currentFile && !selectedFile) {
      const url = currentFile.startsWith('/') ? `${API_BASE_URL}${currentFile}` : currentFile;
      setPreview(url);
    } else if (!currentFile && !selectedFile) {
      setPreview(null);
    }
  }, [currentFile, selectedFile, API_BASE_URL]);

  const handleFileSelect = useCallback((file: File) => {
    // Validar tamaño
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`El archivo no debe exceder ${maxSizeMB}MB`);
      return;
    }

    setSelectedFile(file);
    setFileName(file.name);

    // Crear preview para mostrar (base64 solo para preview local)
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPreview(result);
    };
    reader.readAsDataURL(file);

    // Pasar el File real al componente padre
    onFileChange(file);
  }, [maxSizeMB, onFileChange]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setFileName('');
    setSelectedFile(null);
    onFileChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const isImage = preview?.startsWith('data:image/') || 
    (preview && !preview.startsWith('data:') && /\.(jpg|jpeg|png|gif|webp)$/i.test(preview));
  const isPDF = preview?.startsWith('data:application/pdf') || 
    fileName.toLowerCase().endsWith('.pdf') ||
    (preview && !preview.startsWith('data:') && preview.toLowerCase().endsWith('.pdf'));

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
      
      {preview ? (
        <div className="relative">
          {isImage ? (
            <div className="relative w-full h-64 rounded-lg overflow-hidden border border-gray-200">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              <button
                onClick={handleRemove}
                className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="relative w-full p-4 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {isPDF ? (
                  <FileText className="w-10 h-10 text-red-500" />
                ) : (
                  <FileText className="w-10 h-10 text-gray-400" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900">{fileName || 'Archivo seleccionado'}</p>
                  <p className="text-xs text-gray-500">Click para cambiar</p>
                </div>
              </div>
              <button
                onClick={handleRemove}
                className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                type="button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
          {!isImage && (
            <button
              type="button"
              onClick={handleClick}
              className="mt-2 w-full px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
            >
              <Upload className="w-4 h-4" />
              <span>Cambiar Archivo</span>
            </button>
          )}
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative w-full h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors bg-gray-50 ${
            isDragging
              ? 'border-accent bg-accent/5'
              : 'border-gray-300 hover:border-accent'
          }`}
        >
          <Upload className={`w-8 h-8 mb-2 ${isDragging ? 'text-accent' : 'text-gray-400'}`} />
          <p className="text-sm text-gray-600 mb-1">
            {isDragging ? 'Suelta el archivo aquí' : 'Arrastra un archivo o haz clic para seleccionar'}
          </p>
          <p className="text-xs text-gray-500">
            {accept.includes('pdf') ? 'PDF, imágenes hasta' : 'Hasta'} {maxSizeMB}MB
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleInputChange}
            className="hidden"
          />
        </div>
      )}

      {preview && isImage && (
        <button
          type="button"
          onClick={handleClick}
          className="w-full px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
        >
          <Upload className="w-4 h-4" />
          <span>Cambiar Imagen</span>
        </button>
      )}
    </div>
  );
};

